import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Trash2,
  LogIn,
  Store,
  X,
} from 'lucide-react';
import { financeApi, storeApi } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { createFacebookAuthorizationUrl, readFacebookOAuthResult } from '../lib/facebook-oauth';

interface FacebookAccount {
  id: string;
  accountId: string;
  accountName: string;
  currency: string;
  isActive: boolean;
  storeId: string | null;
  lastSyncAt: string | null;
  createdAt: string;
}

interface PreviewAccount {
  accountId: string;
  accountName: string;
  currency: string;
  isActive: boolean;
}

interface StoreInfo {
  id: string;
  name: string;
  url: string;
  isActive: boolean;
}

interface SyncLog {
  id: string;
  accountId: string;
  status: string;
  recordsCount: number;
  dateFrom: string | null;
  dateTo: string | null;
  error: string | null;
  durationMs: number | null;
  createdAt: string;
}

export function FacebookAdsPage() {
  const { data: facebookConfig, isPending: loadingFacebookConfig, error: facebookConfigError } = useQuery({
    queryKey: ['facebook', 'config'],
    queryFn: async () => (await financeApi.getFacebookConfig()).data,
    retry: 1,
  });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [accounts, setAccounts] = useState<FacebookAccount[]>([]);
  const [stores, setStores] = useState<StoreInfo[]>([]);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Account selection dialog state - single account per store
  const [showSelectDialog, setShowSelectDialog] = useState(false);
  const [previewAccounts, setPreviewAccounts] = useState<PreviewAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [savingSelection, setSavingSelection] = useState(false);
  const [syncingComments, setSyncingComments] = useState(false);
  const [permissionsWarning, setPermissionsWarning] = useState<string | null>(null);
  const oauthResultHandled = useRef(false);

  const handleSyncComments = async () => {
    setSyncingComments(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await financeApi.syncFacebookComments();
      const data = res.data as { success: boolean; message: string; fetched: number; errors?: string[]; missingPermissions?: string[] };
      if (data.missingPermissions?.length) {
        setPermissionsWarning(
          `Brakujące uprawnienia Facebook: ${data.missingPermissions.join(', ')}. ` +
          'Odłącz konto i połącz ponownie. Jeśli problem się powtarza, dodaj siebie jako ' +
          'testera/developera aplikacji w panelu Facebook Developer (developers.facebook.com).'
        );
        setError(data.message);
      } else if (data.fetched > 0) {
        setSuccess(data.message);
      } else if (data.errors?.length) {
        setError(data.message);
      } else {
        setSuccess(data.message);
      }
    } catch (err) {
      setError('Błąd synchronizacji komentarzy: ' + (err instanceof Error ? err.message : 'Nieznany błąd'));
    } finally {
      setSyncingComments(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [accountsRes, logsRes, storesRes] = await Promise.all([
        financeApi.getFacebookAccounts(),
        financeApi.getFacebookSyncLogs(),
        storeApi.getAll(),
      ]);
      const accs = accountsRes.data;
      const logs = logsRes.data;
      const storeList = storesRes.data;
      setAccounts(Array.isArray(accs) ? accs : accs?.accounts || []);
      setSyncLogs(Array.isArray(logs) ? logs : logs?.logs || []);
      setStores(Array.isArray(storeList) ? storeList : []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Nie udało się załadować danych';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Listen for message from FB OAuth popup (multiple methods for browser compatibility)
  useEffect(() => {
    const onResult = (value: unknown) => {
      const data = readFacebookOAuthResult(value);
      if (oauthResultHandled.current || !data) return;
      oauthResultHandled.current = true;
      try { localStorage.removeItem('fb_oauth_result'); } catch { /* noop */ }
      if (data.error) {
        setConnecting(false);
        setSuccess(null);
        setError(`Nie udało się połączyć z Facebookiem: ${data.error}`);
        return;
      }
      handleFacebookCallback(data.code, data.accessToken);
    };

    // Method 1: postMessage from popup window
    const messageHandler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      onResult(event.data);
    };
    window.addEventListener('message', messageHandler);

    // Method 2: BroadcastChannel
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('facebook-oauth');
      bc.onmessage = (event: MessageEvent) => {
        onResult(event.data);
      };
    } catch {
      // BroadcastChannel not supported
    }

    // Method 3: localStorage storage event (cross-tab)
    const storageHandler = (event: StorageEvent) => {
      if (event.key === 'fb_oauth_result' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          onResult(data);
        } catch { /* noop */ }
      }
    };
    window.addEventListener('storage', storageHandler);

    // Method 4: Poll localStorage (most reliable - storage event doesn't fire in same tab)
    const poll = setInterval(() => {
      try {
        const raw = localStorage.getItem('fb_oauth_result');
        if (raw) {
          const data = JSON.parse(raw);
          onResult(data);
        }
      } catch { /* noop */ }
    }, 500);

    return () => {
      window.removeEventListener('message', messageHandler);
      window.removeEventListener('storage', storageHandler);
      clearInterval(poll);
      if (bc) bc.close();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 1: After OAuth, preview accounts (don't save yet)
  const handleFacebookCallback = async (code?: string, accessToken?: string) => {
    try {
      setConnecting(true);
      setError(null);
      setSuccess(null);
      const redirectUri = `${window.location.origin}/facebook-callback.html`;
      const res = await financeApi.previewFacebookAccounts({ code, accessToken, redirectUri });
      const data = res.data;

      // Check if Facebook granted all required permissions
      const perms = data.permissions as { granted?: string[]; declined?: string[]; missing?: string[] } | undefined;
      if (perms?.missing && perms.missing.length > 0) {
        setPermissionsWarning(
          `Facebook nie przyznał uprawnień: ${perms.missing.join(', ')}. ` +
          'Synchronizacja komentarzy może nie działać. ' +
          'Administrator aplikacji powinien sprawdzić dostęp do tych uprawnień w panelu Meta for Developers.'
        );
      } else {
        setPermissionsWarning(null);
      }

      // Show account selection dialog - single select
      setPreviewAccounts(data.accounts || []);
      setConnectionId(data.connectionId);
      setSelectedAccountId(null);
      setSelectedStoreId('');
      setShowSelectDialog(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err instanceof Error ? err.message : 'Błąd podczas łączenia z Facebook');
      setError(msg);
    } finally {
      setConnecting(false);
    }
  };

  // Step 2: Save single selected account with store binding
  const handleSaveSelectedAccounts = async () => {
    if (!connectionId || !selectedAccountId || !selectedStoreId) return;

    try {
      setSavingSelection(true);
      setError(null);
      const res = await financeApi.connectFacebook({
        connectionId,
        selectedAccountIds: [selectedAccountId],
      });
      const data = res.data;

      // Auto-assign store to the newly connected account
      const newAccounts = data.accounts || [];
      if (newAccounts.length > 0) {
        try {
          await financeApi.updateFacebookAccount(newAccounts[0].id, { storeId: selectedStoreId });
        } catch { /* store mapping will be done manually */ }
      }

      setSuccess(data.message || 'Konto reklamowe zostało połączone i przypisane do sklepu.');
      setShowSelectDialog(false);
      setPreviewAccounts([]);
      setConnectionId(null);
      setSelectedAccountId(null);
      setSelectedStoreId('');
      await loadData();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err instanceof Error ? err.message : 'Błąd podczas zapisywania konta');
      setError(msg);
    } finally {
      setSavingSelection(false);
    }
  };

  const connectFacebook = () => {
    if (!facebookConfig?.configured || !facebookConfig.appId) {
      setError(facebookConfigError
        ? 'Nie udało się pobrać konfiguracji Facebooka. Odśwież stronę i spróbuj ponownie.'
        : 'Połączenie z Facebookiem jest chwilowo niedostępne. Skontaktuj się z administratorem aplikacji.');
      return;
    }

    setError(null);
    oauthResultHandled.current = false;
    // Clear any stale OAuth result before starting
    try { localStorage.removeItem('fb_oauth_result'); } catch { /* noop */ }

    const authUrl = createFacebookAuthorizationUrl(facebookConfig.appId, window.location.origin);

    const width = 600;
    const height = 700;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;
    const popup = window.open(authUrl, 'facebook-login', `width=${width},height=${height},left=${left},top=${top}`);
    if (!popup) setError('Przeglądarka zablokowała okno Facebooka. Zezwól na wyskakujące okna dla tej strony i spróbuj ponownie.');
  };

  const disconnectAccount = async (id: string) => {
    try {
      setError(null);
      await financeApi.disconnectFacebook(id);
      setAccounts((prev) => prev.filter((a) => a.id !== id));
      setSuccess('Konto zostało odłączone.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Błąd podczas odłączania konta';
      setError(msg);
    }
  };

  const updateStoreMapping = async (accountDbId: string, storeId: string | null) => {
    try {
      setError(null);
      await financeApi.updateFacebookAccount(accountDbId, { storeId });
      setAccounts((prev) =>
        prev.map((a) => (a.id === accountDbId ? { ...a, storeId } : a))
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Błąd podczas mapowania sklepu';
      setError(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 text-accent-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Facebook</h1>
          <p className="text-text-secondary mt-1">
            Połącz konto Facebook, aby importować dane o reklamach, komentarze i zarządzać integracją. Jedno konto = jeden sklep.
          </p>
        </div>
        <button
          onClick={connectFacebook}
          disabled={connecting || loadingFacebookConfig}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-text-primary bg-[#1877F2] hover:bg-[#166FE5] transition-colors disabled:opacity-50"
        >
          {connecting || loadingFacebookConfig ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <LogIn className="w-4 h-4" />
          )}
          {connecting ? 'Łączenie...' : 'Połącz z Facebook'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="card border-danger/30 bg-danger/5">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-danger flex-shrink-0" />
            <p className="text-danger text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="card border-success/30 bg-green-500/5">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
            <p className="text-success text-sm">{success}</p>
          </div>
        </div>
      )}

      {/* Permissions Warning */}
      {permissionsWarning && (
        <div className="card border-warning/30 bg-yellow-500/5">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-warning text-sm font-medium mb-1">Brakujące uprawnienia Facebook</p>
              <p className="text-warning/80 text-sm">{permissionsWarning}</p>
            </div>
          </div>
        </div>
      )}

      {/* Account Selection Dialog (Modal) - Single account + store */}
      {showSelectDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
          onKeyDown={(e) => { if (e.key === 'Escape') { setShowSelectDialog(false); setPreviewAccounts([]); setConnectionId(null); } }}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <div className="bg-surface-raised rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col mx-4 animate-scale-in">
            {/* Dialog Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Połącz konto Facebook ze sklepem</h2>
                <p className="text-sm text-text-secondary mt-0.5">
                  Wybierz jedno konto reklamowe i przypisz je do sklepu. Jedno konto = jeden sklep.
                </p>
              </div>
              <button
                onClick={() => { setShowSelectDialog(false); setPreviewAccounts([]); setConnectionId(null); }}
                className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Dialog Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Store selector - mandatory */}
              <div>
                <label className="text-xs text-text-secondary mb-1.5 block font-medium">Przypisz do sklepu *</label>
                <select
                  value={selectedStoreId}
                  onChange={(e) => setSelectedStoreId(e.target.value)}
                  className="w-full bg-surface-base border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-accent-primary/50 transition-colors"
                >
                  <option value="">— Wybierz sklep —</option>
                  {stores.filter(s => s.isActive).map((store) => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>

              {/* Account list - radio buttons (single select) */}
              <div>
                <label className="text-xs text-text-secondary mb-1.5 block font-medium">Wybierz konto reklamowe *</label>
                <div className="space-y-2">
                  {previewAccounts.map((acc) => (
                    <label
                      key={acc.accountId}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                        selectedAccountId === acc.accountId
                          ? 'bg-accent-primary/5 border-accent-primary/30'
                          : 'bg-surface-base/30 border-border-subtle hover:border-border'
                      }`}
                    >
                      <input
                        type="radio"
                        name="fb-account"
                        checked={selectedAccountId === acc.accountId}
                        onChange={() => setSelectedAccountId(acc.accountId)}
                        className="w-4 h-4 border-gray-600 bg-surface-hover text-accent-primary focus:ring-accent-primary"
                      />
                      <div className="w-8 h-8 rounded-lg bg-[#1877F2]/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-[#1877F2]">f</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">{acc.accountName}</p>
                        <p className="text-xs text-text-muted">{acc.accountId} · {acc.currency}</p>
                      </div>
                      {acc.isActive ? (
                        <span className="text-xs text-success bg-success/10 px-2 py-0.5 rounded-full">Aktywne</span>
                      ) : (
                        <span className="text-xs text-text-muted bg-surface-hover px-2 py-0.5 rounded-full">Nieaktywne</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3 text-xs text-text-secondary">
                <p className="font-medium text-accent-info mb-1">Co zostanie połączone?</p>
                <p>Dane reklamowe (wydatki, zasięg, konwersje) do modułu Finanse.</p>
                <p>Komentarze pod postami do modułu Wiadomości.</p>
                <p>Wszystko powiązane z wybranym sklepem.</p>
              </div>
            </div>

            {/* Dialog Footer - CTA left, Cancel right */}
            <div className="flex gap-3 px-6 py-4 border-t border-border-subtle">
              <button
                onClick={handleSaveSelectedAccounts}
                disabled={savingSelection || !selectedAccountId || !selectedStoreId}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {savingSelection && <Loader2 className="w-4 h-4 animate-spin" />}
                Połącz konto
              </button>
              <button
                onClick={() => { setShowSelectDialog(false); setPreviewAccounts([]); setConnectionId(null); }}
                className="btn-secondary flex-1"
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No accounts state */}
      {accounts.length === 0 && !error && (
        <div className="card border-border-subtle">
          <EmptyState
            icon={LogIn}
            title="Brak połączonych kont"
            description={'Kliknij \u201ePoł\u0105cz z Facebook\u201d, aby zalogowa\u0107 si\u0119 na swoje konto Facebook i wybra\u0107 konta reklamowe do po\u0142\u0105czenia.'}
            iconColorClass="from-[#1877F2]/20 to-[#1877F2]/10 border-[#1877F2]/20 text-[#1877F2]"
          />
        </div>
      )}

      {/* Connected accounts with store mapping */}
      {accounts.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-text-primary">Połączone konta ({accounts.length})</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSyncComments}
                disabled={syncingComments}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-text-secondary hover:text-text-primary bg-surface-hover/30 border border-border-subtle hover:border-accent-primary/30 transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncingComments ? 'animate-spin' : ''}`} />
                {syncingComments ? 'Synchronizuję...' : 'Pobierz komentarze'}
              </button>
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-text-secondary hover:text-text-primary bg-surface-hover/30 border border-border-subtle hover:border-accent-primary/30 transition-all"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Odśwież
              </button>
            </div>
          </div>

          <div className="grid gap-3">
            {accounts.map((account) => (
              <div key={account.id} className="card hover:border-accent-primary/20 transition-all">
                <div className="flex items-center justify-between gap-4">
                  {/* Account info */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      account.isActive ? 'bg-[#1877F2]/20' : 'bg-surface-hover'
                    }`}>
                      <span className="text-lg font-bold text-[#1877F2]">f</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-text-primary font-medium truncate">{account.accountName || account.accountId}</p>
                      <p className="text-text-muted text-xs">{account.accountId} · {account.currency}</p>
                    </div>
                  </div>

                  {/* Store mapping */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Store className="w-4 h-4 text-text-muted" />
                    <select
                      value={account.storeId || ''}
                      onChange={(e) => updateStoreMapping(account.id, e.target.value || null)}
                      className="bg-surface-base border border-border rounded-lg px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:border-accent-primary/50 transition-colors min-w-[180px]"
                    >
                      <option value="">— Bez przypisania —</option>
                      {stores.filter(s => s.isActive).map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status & actions */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {account.lastSyncAt && (
                      <div className="flex items-center gap-1.5 text-xs text-text-muted">
                        <Clock className="w-3 h-3" />
                        {new Date(account.lastSyncAt).toLocaleString('pl-PL')}
                      </div>
                    )}
                    {account.isActive ? (
                      <span className="flex items-center gap-1 text-xs text-success bg-success/10 px-2 py-1 rounded-full">
                        <CheckCircle className="w-3 h-3" />
                        Aktywne
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-text-muted bg-surface-hover px-2 py-1 rounded-full">
                        <XCircle className="w-3 h-3" />
                        Nieaktywne
                      </span>
                    )}
                    <button
                      onClick={() => disconnectAccount(account.id)}
                      className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-danger/10 transition-all"
                      title="Odłącz konto"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync logs */}
      {syncLogs.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Historia synchronizacji</h2>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="text-left text-text-muted font-medium py-2 px-3">Data</th>
                  <th className="text-left text-text-muted font-medium py-2 px-3">Konto</th>
                  <th className="text-left text-text-muted font-medium py-2 px-3">Status</th>
                  <th className="text-right text-text-muted font-medium py-2 px-3">Rekordów</th>
                  <th className="text-right text-text-muted font-medium py-2 px-3">Czas</th>
                </tr>
              </thead>
              <tbody>
                {syncLogs.map((log) => (
                  <tr key={log.id} className="border-b border-border-subtle last:border-0">
                    <td className="py-2 px-3 text-text-secondary">
                      {new Date(log.createdAt).toLocaleString('pl-PL')}
                    </td>
                    <td className="py-2 px-3 text-text-secondary">{log.accountId}</td>
                    <td className="py-2 px-3">
                      {log.status === 'success' && (
                        <span className="text-success flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" /> Sukces
                        </span>
                      )}
                      {log.status === 'failed' && (
                        <span className="text-danger flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5" /> Błąd
                        </span>
                      )}
                      {log.status === 'partial' && (
                        <span className="text-warning flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" /> Częściowy
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right text-text-secondary">{log.recordsCount}</td>
                    <td className="py-2 px-3 text-right text-text-muted">
                      {log.durationMs ? `${(log.durationMs / 1000).toFixed(1)}s` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
